// Pure helpers extracted from ExternalManagerPortfoliosSelector so they
// can be unit-tested without rendering React. Keep these dependency-free
// — no React imports, no remote/network calls, no DataController.

/**
 * Resolve the mode (editor/video) to push to BuilderPage for a member of
 * a group.
 *
 * Reads straight from videoModes for both Linked and Individual modes.
 * The Linked-group invariant ("everyone in the group shares the same
 * contentType") is maintained by the *writers* — toggleGroupMode and
 * syncGroupContent both write to videoModes for every member of the
 * group. So a single source of truth (videoModes) suffices.
 */
export function resolveGroupMemberMode(group, member, videoModes) {
    if (!group || !member) return 'editor';
    return (videoModes && videoModes[member.fundNumber]) || 'editor';
}

/**
 * The group's effective mode for header / icon display. Derives from the
 * primary member's videoModes entry. Caller decides whether to render a
 * group-level mode toggle at all (typically only in Linked mode).
 */
export function groupModeOf(group, members, videoModes) {
    if (!group || !members || members.length === 0) return 'editor';
    const primary = members[0];
    return (videoModes && primary && videoModes[primary.fundNumber]) || 'editor';
}

/**
 * Strip per-fund status fields out of a fund's content payload before
 * fanning it out to other members of a Linked group. managerComplete is
 * each fund's own Pending/Ready flag — it must not propagate.
 */
export function filterContentForSync(data) {
    if (!data || typeof data !== 'object') return {};
    const out = {};
    for (const [k, v] of Object.entries(data)) {
        if (k === 'managerComplete') continue;
        if (k === 'TICKERLIST') continue; // per-ticker identity — copying the primary's misdirects later saves
        out[k] = v;
    }
    return out;
}

/**
 * Pick the subset of a portfolio pool that belongs to a group, preserving
 * the pool's iteration order. The "primary" used for Linked-mode content
 * source is conventionally members[0].
 */
export function findGroupMembers(group, pool) {
    if (!group || !pool) return [];
    const set = new Set(group.fundNumbers || []);
    return pool.filter(p => p && set.has(p.fundNumber));
}

/**
 * Partition a list of FundDTOs into pending/ready pools by the parallel
 * retrievev2 results, and build the initial videoModes dict from each
 * fund's stored contentType.
 *
 *   portfolios[i] ↔ results[i]
 *   results[i].managerComplete === 'ready' → ready, otherwise pending
 *   results[i].contentType (default 'editor') → videoModes[fundNumber]
 */
export function partitionByManagerComplete(portfolios, results) {
    const pending = [];
    const ready = [];
    const videoModes = {};
    for (let i = 0; i < (portfolios || []).length; i++) {
        const p = portfolios[i];
        const r = (results && results[i]) || {};
        videoModes[p.fundNumber] = r.contentType || 'editor';
        if (r.managerComplete === 'ready') ready.push(p);
        else pending.push(p);
    }
    return { pending, ready, videoModes };
}

// ── Move-to-Ready empty-content pre-check ──────────────────────────────
// Default video data-field keys; mirror contents/VideoDataFields.js so a
// template without an explicit VideoDataFields config still resolves.
const DEFAULT_VIDEO_ID_FIELD = 'videoId';
const DEFAULT_VIDEO_UPLOAD_DATE_FIELD = 'videoUploadDate';
// className set treated as manager-editable text (matches VideoUploadPanel's
// field-finder). Slate content lives under each block's `dataName`.
const EDITABLE_TEXT_CLASSNAMES = ['EditableText', 'FinalisedEditableText'];

function slateNodeHasContent(node) {
    if (!node || typeof node !== 'object') return false;
    const type = typeof node.type === 'string' ? node.type : '';
    // Embedded non-text blocks (table-wrapper, image-wrapper, chart-wrapper, …)
    // count as content by their presence. Plain containers (paragraph, text,
    // heading, span, list-item) only count if they hold non-whitespace text.
    if (type.endsWith('-wrapper')) return true;
    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            if (typeof child.text === 'string' && child.text.trim().length > 0) return true;
            if (child && Array.isArray(child.children) && slateNodeHasContent(child)) return true;
        }
    }
    return false;
}

/**
 * True when a Slate field holds no real content. Accepts a JSON string or a
 * node array; anything non-array / unparseable is treated as empty. The
 * default `[{type:'text',children:[{text:''}]}]` is empty.
 */
export function isSlateContentEmpty(value) {
    let nodes = value;
    if (typeof value === 'string') {
        try { nodes = JSON.parse(value); } catch (e) { return true; }
    }
    if (!Array.isArray(nodes)) return true;
    return !nodes.some(slateNodeHasContent);
}

/**
 * Walk a template Document and collect the dataNames of every manager-editable
 * Slate field:
 *  - fixed/relative pages: EditableText / FinalisedEditableText blocks in
 *    `content[]` (dataName under `configuration`, recursing nested `content`);
 *  - flowing pages: the page-level `flowContent.dataName`.
 * Result is de-duped.
 */
export function collectEditableDataNames(templateDoc) {
    const names = new Set();
    const walk = (items) => {
        if (!Array.isArray(items)) return;
        for (const item of items) {
            if (!item || typeof item !== 'object') continue;
            // dataName lives under configuration (Page.js passes content.configuration
            // as the component's config; EditableText reads config.dataName). Fall back
            // to a top-level dataName for safety.
            const dn = (item.configuration && item.configuration.dataName) || item.dataName;
            if (EDITABLE_TEXT_CLASSNAMES.includes(item.className) && dn) {
                names.add(dn);
            }
            if (Array.isArray(item.content)) walk(item.content);
        }
    };
    const pages = (templateDoc && templateDoc.pages) || [];
    for (const page of pages) {
        if (!page) continue;
        // Flowing pages keep their editable Slate body at the page level.
        if (page.flowContent && page.flowContent.dataName) names.add(page.flowContent.dataName);
        walk(page.content);
    }
    return [...names];
}

/**
 * Resolve the video data-field keys from a template's VideoDataFields block
 * (config overrides), falling back to the defaults.
 */
export function resolveVideoFieldNames(templateDoc) {
    let idField = DEFAULT_VIDEO_ID_FIELD;
    let uploadDateField = DEFAULT_VIDEO_UPLOAD_DATE_FIELD;
    const walk = (items) => {
        if (!Array.isArray(items)) return;
        for (const item of items) {
            if (!item || typeof item !== 'object') continue;
            if (item.className === 'VideoDataFields') {
                const cfg = item.configuration || item;
                if (cfg.videoIdDataName) idField = cfg.videoIdDataName;
                if (cfg.videoUploadDateDataName) uploadDateField = cfg.videoUploadDateDataName;
            }
            if (Array.isArray(item.content)) walk(item.content);
        }
    };
    const pages = (templateDoc && templateDoc.pages) || [];
    for (const page of pages) walk(page && page.content);
    return { idField, uploadDateField };
}

function isBlank(v) {
    return v == null || (typeof v === 'string' && v.trim().length === 0);
}

/**
 * Given a group's members, their fetched content blobs, the template's editable
 * dataNames and video field names, return the members whose content is empty
 * for their contentType.
 *
 *   members:        [{ fundNumber, fundName }]
 *   contentByFund:  { [fundNumber]: retrievev2-blob }  (contentType + data fields)
 *   editableDataNames: string[]   (editor rule: ANY empty flags)
 *   videoFieldNames:   { idField, uploadDateField }
 * → [{ fundNumber, fundName, reason }]
 */
export function findIncompleteGroupMembers({ members, contentByFund, editableDataNames, videoFieldNames }) {
    const out = [];
    const editable = editableDataNames || [];
    const { idField, uploadDateField } = videoFieldNames || {};
    for (const m of members || []) {
        const blob = (contentByFund && contentByFund[m.fundNumber]) || {};
        const contentType = blob.contentType || 'editor';
        if (contentType === 'video') {
            if (isBlank(blob[idField]) || isBlank(blob[uploadDateField])) {
                out.push({ fundNumber: m.fundNumber, fundName: m.fundName, reason: 'Video is missing' });
            }
        } else {
            // editor: flag if the template has editable blocks and ANY is empty
            if (editable.length > 0 && editable.some(dn => isSlateContentEmpty(blob[dn]))) {
                out.push({ fundNumber: m.fundNumber, fundName: m.fundName, reason: 'Commentary is empty' });
            }
        }
    }
    return out;
}
